import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PlusSquare, CalendarClock, History, ArrowRight, LayoutTemplate, BarChart3, Clock3, Trophy, Upload } from "lucide-react";
import { SectionHeader } from "@/util-components/page-layout/section-header";
import { DataViewGridCard, GenericItem } from "@/util-components/data-view/generic-card";
import { StoreCtaBanner } from "./components/StoreCtaBanner";
import { UploadGraphicCard } from "./components/UploadGraphicCard";
import { UploadGraphicDialog } from "./components/UploadGraphicDialog";
import { usePostsGetTemplateInsights, usePostsGetPlannedInsights, usePostsGetRecentInsights } from "@/lib/api/requests/posts.requests";
import { MostUsedTemplate, PlannedPost, RecentPost } from "@/lib/api/interfaces/posts.interface";
import { formatFriendlyDate } from "@/lib/utils/date-formatter";
import { Skeleton } from "@/ui-components/ui/skeleton";
import { Button } from "@/ui-components/ui/button";
import { cn } from "@/lib/utils/cn";
import { getSocialIcon } from "../utils/socialUtils";
import { PlannedPostDialog } from "../components/PlannedPostDialog";
import { RecentPostDialog } from "../components/RecentPostDialog";
import { UpgradePlanDialog } from "@/util-components/dialog/UpgradePlanDialog";
import { PlanKey } from "@/lib/api/interfaces/billing.interface";
import { Permission } from "@/lib/api/interfaces/permissions.interface";
import { usePermissions } from "@/lib/hooks/use-permissions";
import { usePageState } from "@/lib/hooks/use-page-state";
import { EntityPreviewFrame } from "@/util-components/data-view";

const CARD_CLASS_NAME = "w-full rounded-xl border-gray-200";
const START_GRID_CLASS_NAME = "grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6";

const EmptyPreview = () => (
  <div className="flex h-full w-full items-center justify-center text-gray-300">
    <LayoutTemplate className="h-9 w-9" />
  </div>
);

const StatusLabel = ({ children }: { children: React.ReactNode }) => (
  <span className="text-[11px] font-medium text-gray-500">{children}</span>
);

const PlatformPill = ({ children }: { children: React.ReactNode }) => (
  <div className="flex h-6 w-6 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500">
    {children}
  </div>
);

const StartStatCard = ({
  label,
  value,
  detail,
  icon: Icon,
  isLoading,
  variant = "blue",
}: {
  label: string;
  value: string;
  detail: string;
  icon: React.ElementType;
  isLoading?: boolean;
  variant?: "blue" | "indigo" | "sky";
}) => {
  const variants = {
    blue: {
      bg: "bg-gradient-to-br from-white to-blue-100/40",
      iconBg: "bg-blue-100/50 text-blue-600 border-blue-200/50",
    },
    indigo: {
      bg: "bg-gradient-to-br from-white to-indigo-100/40",
      iconBg: "bg-indigo-100/50 text-indigo-600 border-indigo-200/50",
    },
    sky: {
      bg: "bg-gradient-to-br from-white to-sky-100/40",
      iconBg: "bg-sky-100/50 text-sky-600 border-sky-200/50",
    },
  };

  const style = variants[variant];

  return (
    <div className={cn("relative overflow-hidden rounded-xl border border-gray-200 p-4 shadow-sm", style.bg)}>
      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-4 w-24 rounded-full" />
          <Skeleton className="h-7 w-16 rounded-full" />
          <Skeleton className="h-3 w-32 rounded-full" />
        </div>
      ) : (
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{label}</div>
            <div className="mt-1 text-2xl font-bold tracking-tight text-gray-900">{value}</div>
            <div className="mt-1 truncate text-xs font-medium text-gray-500">{detail}</div>
          </div>
          <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border", style.iconBg)}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      )}
    </div>
  );
};

const getStartGridItemLimit = () => {
  if (typeof window === "undefined") return 6;
  if (window.matchMedia("(min-width: 1280px)").matches) return 6;
  return window.matchMedia("(min-width: 1024px)").matches ? 5 : 6;
};

const useStartGridItemLimit = () => {
  const [limit, setLimit] = useState(getStartGridItemLimit);

  useEffect(() => {
    const lgQuery = window.matchMedia("(min-width: 1024px)");
    const xlQuery = window.matchMedia("(min-width: 1280px)");

    const onChange = () => setLimit(getStartGridItemLimit());

    onChange();
    lgQuery.addEventListener("change", onChange);
    xlQuery.addEventListener("change", onChange);

    return () => {
      lgQuery.removeEventListener("change", onChange);
      xlQuery.removeEventListener("change", onChange);
    };
  }, []);

  return limit;
};

const StartPageHeader = ({ onUploadClick }: { onUploadClick: () => void }) => (
  <div className="flex flex-col gap-5 border-b border-gray-200 pb-6 lg:flex-row lg:items-end lg:justify-between">
    <div className="max-w-2xl">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">Post Generator</div>
      <h1 className="text-2xl font-semibold tracking-tight text-gray-950 sm:text-3xl">
        Content planen, erstellen und veröffentlichen
      </h1>
      <p className="mt-2 text-sm leading-6 text-gray-600">
        Starte mit einer eigenen Grafik, nutze bewährte Templates oder prüfe, was als Nächstes in deinem Content-Kalender ansteht.
      </p>
    </div>

    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
      <Button
        type="button"
        onClick={onUploadClick}
        className="h-10 bg-gray-950 px-4 text-white hover:bg-gray-800"
      >
        <Upload className="mr-2 h-4 w-4" />
        Grafik hochladen
      </Button>
      <Button asChild variant="outline" className="h-10 border-gray-200 bg-white px-4 text-gray-700 hover:bg-gray-50 hover:text-gray-950">
        <Link to="/post-generator/templates">
          Templates ansehen
          <ArrowRight className="ml-2 h-4 w-4" />
        </Link>
      </Button>
    </div>
  </div>
);


interface PostGeneratorStartPageProps {
  isMockMode?: boolean;
  mockMostUsedTemplates?: GenericItem[];
  mockPlannedPosts?: GenericItem[];
  mockPublishedPosts?: GenericItem[];
  onUploadClick?: () => void;
  onItemClick?: (id: string) => void;
}

/**
 * The new default landing page for the Post Generator.
 * Displays a CTA banner and three main sections:
 * 1. Create Post (Upload + Most Used Templates)
 * 2. Planned Posts
 * 3. Published Posts
 */
export const PostGeneratorStartPage = ({
  isMockMode = false,
  mockMostUsedTemplates = [],
  mockPlannedPosts = [],
  mockPublishedPosts = [],
  onUploadClick,
  onItemClick
}: PostGeneratorStartPageProps) => {
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [selectedPlannedPost, setSelectedPlannedPost] = useState<PlannedPost | null>(null);
  const [isPlannedDialogOpen, setIsPlannedDialogOpen] = useState(false);
  const [selectedRecentPost, setSelectedRecentPost] = useState<RecentPost | null>(null);
  const [isRecentDialogOpen, setIsRecentDialogOpen] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { state: pageState, setSettings } = usePageState<{ isStoreCtaVisible?: boolean }>("post-generator-start", {
    settings: { isStoreCtaVisible: true },
  });
  const isStoreCtaVisible = pageState.settings?.isStoreCtaVisible ?? true;
  const visibleGridItemLimit = useStartGridItemLimit();

  const handleUploadClick = () => {
    setIsUploadDialogOpen(true);
    onUploadClick?.();
  };

  const handleContinueUpload = (file: File) => {
    const customImageUrl = URL.createObjectURL(file);
    setIsUploadDialogOpen(false);
    navigate('/post-generator/create-post', { state: { customImageUrl } });
  };

  // ----- Data Fetching -----
  // We only fetch if NOT in mock mode
  const { data: templatesData, isLoading: isLoadingTemplates } = usePostsGetTemplateInsights(
    { page: 0, limit: 5 },
    { enabled: !isMockMode }
  );

  const { data: plannedData, isLoading: isLoadingPlanned } = usePostsGetPlannedInsights(
    { page: 0, limit: 6 },
    { enabled: !isMockMode }
  );

  const { data: recentData, isLoading: isLoadingRecent } = usePostsGetRecentInsights(
    { page: 0, limit: 6 },
    { enabled: !isMockMode }
  );


  // ----- Mappers -----
  const mapTemplateToGenericItem = (t: MostUsedTemplate): GenericItem => {
    // Custom templates (originatesFrom == null) are restricted for users without MANAGE_CUSTOMER_TEMPLATE permission
    const isCustom = !t.importedFromTemplateId;
    const hasPermission = can(Permission.MANAGE_CUSTOMER_TEMPLATE);
    const isDisabled = isCustom && !hasPermission;

    return {
    id: t.templateId,
    title: t.name,
    previewContent: (
      <EntityPreviewFrame disabled={isDisabled} backgroundUrl={t.thumbnailUrl}>
        {t.thumbnailUrl ? (
          <img 
            src={t.thumbnailUrl} 
            alt={t.name} 
            className="max-h-full max-w-full rounded-sm object-contain"
            loading="lazy"
          />
        ) : (
          <EmptyPreview />
        )}
      </EntityPreviewFrame>
    ),
    metadata: {
      badgeText: `${t.count}x genutzt`,
      usageCount: t.count,
      isDisabled // Store this to control actions
    }
  }};

  const mapPlannedToGenericItem = (p: PlannedPost): GenericItem => {
    const isFailed = p.status === 'FAILED';
    const previewUrl = p.thumbnailUrl || p.thumbnailPath || p.template?.thumbnailUrl;

    return {
      id: p._id,
      title: p.message ? p.message.substring(0, 30) + (p.message.length > 30 ? '...' : '') : 'Geplanter Post',
      previewContent: (
        <EntityPreviewFrame backgroundUrl={previewUrl}>
          {(p.thumbnailUrl || p.thumbnailPath) ? (
            <img 
              src={p.thumbnailUrl || p.thumbnailPath} 
              alt={p.message || 'Geplanter Post'} 
              className="max-h-full max-w-full rounded-sm object-contain"
              loading="lazy"
            />
          ) : p.template?.thumbnailUrl ? (
            <img 
              src={p.template.thumbnailUrl} 
              alt={p.message || 'Geplanter Post'} 
              className="max-h-full max-w-full rounded-sm object-contain"
              loading="lazy"
            />
          ) : (
            <EmptyPreview />
          )}
        </EntityPreviewFrame>
      ),
      metadata: {
        badgeText: isFailed ? 'Fehler' : 'Geplant',
        footer: (
          <StatusLabel>{formatFriendlyDate(p.scheduledFor)}</StatusLabel>
        ),
        platforms: [
          (p.options?.instagramPost || p.options?.instagramStory) && (
            <PlatformPill key="ig">
              {getSocialIcon("INSTAGRAM", "h-3.5 w-3.5 text-gray-500")}
            </PlatformPill>
          ),
          (p.options?.facebookPost || p.options?.facebookStory) && (
            <PlatformPill key="fb">
              {getSocialIcon("FACEBOOK", "h-3.5 w-3.5 text-gray-500")}
            </PlatformPill>
          ),
          (p.options?.whatsappPost || (p.whatsappGroupIds && p.whatsappGroupIds.length > 0)) && (
            <PlatformPill key="wa">
              {getSocialIcon("WHATSAPP", "h-3.5 w-3.5 text-gray-500")}
            </PlatformPill>
          ),
          !(p.options?.instagramPost || p.options?.instagramStory || p.options?.facebookPost || p.options?.facebookStory || p.options?.whatsappPost || (p.whatsappGroupIds && p.whatsappGroupIds.length > 0)) && (
            <PlatformPill key="none">
              <LayoutTemplate className="w-3.5 h-3.5" />
            </PlatformPill>
          )
        ].filter(Boolean) as React.ReactNode[]
      }
    };
  };

  const mapRecentToGenericItem = (r: RecentPost): GenericItem => ({
    id: r._id,
    title: r.message ? r.message.substring(0, 30) + (r.message.length > 30 ? '...' : '') : (r.template?.name || 'Post'),
    previewContent: (
      <EntityPreviewFrame backgroundUrl={r.thumbnailUrl || r.template?.thumbnailUrl}>
        {r.thumbnailUrl ? (
           <img src={r.thumbnailUrl} alt="Vorschau" className="max-h-full max-w-full rounded-sm object-contain" loading="lazy" />
        ) : r.template?.thumbnailUrl ? (
           <img src={r.template.thumbnailUrl} alt="Vorschau" className="max-h-full max-w-full rounded-sm object-contain" loading="lazy" />
        ) : (
           <EmptyPreview />
        )}
      </EntityPreviewFrame>
    ),
    metadata: {
      badgeText: "Gepostet",
      footer: (
        <StatusLabel>{formatFriendlyDate(r.createdAt)}</StatusLabel>
      ),
      platforms: [
        r.origin ? (
          <PlatformPill key="origin">
            {getSocialIcon(r.origin, "h-3.5 w-3.5 text-gray-500")}
          </PlatformPill>
        ) : (
           <PlatformPill key="placeholder">
              <LayoutTemplate className="w-3.5 h-3.5" />
           </PlatformPill>
        )
      ]
    }
  });

  // ----- Helper for extraction -----
  const extractData = (res: any) => res?.docs || res?.data || [];

  // ----- Final Data Arrays -----
  const templatesToDisplay = isMockMode ? mockMostUsedTemplates : extractData(templatesData).map(mapTemplateToGenericItem);
  const plannedToDisplay = isMockMode ? mockPlannedPosts : extractData(plannedData).map(mapPlannedToGenericItem);
  const publishedToDisplay = isMockMode ? mockPublishedPosts : extractData(recentData).map(mapRecentToGenericItem);
  const templateGridItemLimit = Math.max(visibleGridItemLimit - 1, 0);
  const templatesVisible = templatesToDisplay.slice(0, templateGridItemLimit);
  const plannedVisible = plannedToDisplay.slice(0, visibleGridItemLimit);
  const publishedVisible = publishedToDisplay.slice(0, visibleGridItemLimit);
  const firstSelectableTemplateId = templatesToDisplay.find((template: any) => !template.metadata?.isDisabled)?.id;
  const topTemplate = useMemo(() => {
    const templates = templatesToDisplay as GenericItem[];
    return templates.reduce<GenericItem | null>((current, template) => {
      const currentCount = current?.metadata?.usageCount ?? 0;
      const nextCount = template.metadata?.usageCount ?? 0;
      return nextCount > currentCount ? template : current;
    }, templates[0] ?? null);
  }, [templatesToDisplay]);

  const stats = [
    {
      label: "Geplant",
      value: String(isMockMode ? plannedToDisplay.length : plannedData?.count ?? 0),
      detail: "Posts in der Pipeline",
      icon: Clock3,
      isLoading: !isMockMode && isLoadingPlanned,
      variant: "indigo" as const,
    },
    {
      label: "Veröffentlicht",
      value: String(isMockMode ? publishedToDisplay.length : recentData?.count ?? 0),
      detail: "Posts im Verlauf",
      icon: BarChart3,
      isLoading: !isMockMode && isLoadingRecent,
      variant: "blue" as const,
    },
    {
      label: "Top-Template",
      value: topTemplate?.metadata?.usageCount ? `${topTemplate.metadata.usageCount}x` : "-",
      detail: topTemplate?.title ?? "Noch keine Nutzung",
      icon: Trophy,
      isLoading: !isMockMode && isLoadingTemplates,
      variant: "sky" as const,
    },
  ];

  const handleItemClick = onItemClick || ((id: string) => {
    // Basic navigation fallback, to be enhanced based on context if needed
    console.log("Clicked item", id);
  });

  const handlePlannedPostClick = (id: string) => {
    const post = extractData(plannedData).find((p: any) => p._id === id);
    if (post) {
      setSelectedPlannedPost(post);
      setIsPlannedDialogOpen(true);
    }
  };

  const handleRecentPostClick = (id: string) => {
    const post = extractData(recentData).find((p: any) => p._id === id);
    if (post) {
      setSelectedRecentPost(post);
      setIsRecentDialogOpen(true);
    }
  };

  // Shared actions config for the Generic Cards
  const mockActions = {
    onItemClick: handleItemClick,
  };

  return (
    <div className="h-full w-full overflow-y-auto bg-gray-50">
      <div className="mx-auto w-full max-w-7xl space-y-8 p-4 pb-24 sm:p-6 lg:p-8" data-onboarding="post-generator-start-page">
        <StartPageHeader onUploadClick={handleUploadClick} />

        <div className="grid gap-3 md:grid-cols-3">
          {stats.map((stat) => (
            <StartStatCard key={stat.label} {...stat} />
          ))}
        </div>

        {isStoreCtaVisible && (
          <StoreCtaBanner onClose={() => setSettings({ isStoreCtaVisible: false })} />
        )}

        {/* Section 1: Neuen Post erstellen */}
        <section className="space-y-6 pt-4" data-onboarding="post-generator-start-section">
          <SectionHeader
            icon={PlusSquare}
            title="Neuen Post erstellen"
            description="Lade dein eigenes Design hoch oder wähle aus oft genutzten Templates."
            rightElement={
              <Link to="/post-generator/templates" className="flex items-center gap-1 text-sm font-semibold text-gray-600 transition-colors hover:text-gray-950">
                Deine Templates
                <ArrowRight className="w-4 h-4" />
              </Link>
            }
          />
          <div className={START_GRID_CLASS_NAME}>
            <div className="h-full" data-onboarding="post-generator-entry-upload">
              <UploadGraphicCard onClick={handleUploadClick} />
            </div>
            <UploadGraphicDialog
              isOpen={isUploadDialogOpen}
              onOpenChange={setIsUploadDialogOpen}
              onContinue={handleContinueUpload}
            />
            
            {/* Loading State or Data */}
            {!isMockMode && isLoadingTemplates ? (
              Array.from({ length: templateGridItemLimit }).map((_, i) => (
                <div key={i} className="flex flex-col gap-3">
                  <Skeleton className="w-full aspect-square rounded-xl" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-3/4 rounded-full" />
                    <Skeleton className="h-3 w-1/2 rounded-full" />
                  </div>
                </div>
              ))
            ) : (
              templatesVisible.map((template: any) => {
                const isDisabled = template.metadata?.isDisabled;
                
                return (
                <div
                  key={template.id}
                  className="h-full"
                  data-onboarding={!isDisabled && template.id === firstSelectableTemplateId ? "post-generator-entry-template" : undefined}
                >
                  <DataViewGridCard 
                    item={template} 
                    actions={{
                      ...mockActions,
                      onItemClick: isDisabled 
                        ? () => setShowUpgradeDialog(true)
                        : () => navigate(`/post-generator/create-post?templateId=${template.id}`)
                    }}
                    className={cn(CARD_CLASS_NAME, isDisabled && "opacity-75")}
                  />
                </div>
              )})
            )}
          </div>
        </section>

        {/* Section 2: Geplante Posts */}
        <section className="space-y-6 pt-6">
          <SectionHeader
            icon={CalendarClock}
            title="Geplante Posts"
            description="Deine anstehenden Veröffentlichungen."
            rightElement={
              <Link to="/post-generator/planned" className="flex items-center gap-1 text-sm font-semibold text-gray-600 transition-colors hover:text-gray-950">
                Alle geplanten Posts
                <ArrowRight className="w-4 h-4" />
              </Link>
            }
          />
           <div className={START_GRID_CLASS_NAME}>
            {/* Loading State or Data */}
            {!isMockMode && isLoadingPlanned ? (
              Array.from({ length: visibleGridItemLimit }).map((_, i) => (
                <div key={i} className="flex flex-col gap-3">
                  <Skeleton className="w-full aspect-square rounded-xl" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-3/4 rounded-full" />
                    <Skeleton className="h-3 w-1/2 rounded-full" />
                  </div>
                </div>
              ))
            ) : plannedVisible.length > 0 ? (
               plannedVisible.map((post: any) => (
                <DataViewGridCard 
                  key={post.id} 
                  item={post} 
                  actions={{
                    ...mockActions,
                    onItemClick: () => handlePlannedPostClick(post.id)
                  }}
                  className={CARD_CLASS_NAME}
                />
              ))
            ) : (
              <div className="col-span-full flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 bg-white py-12 text-center">
                 <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg border border-gray-200 bg-gray-50">
                   <CalendarClock className="h-5 w-5 text-gray-400" />
                 </div>
                 <h3 className="mb-1 text-sm font-semibold text-gray-900">Keine geplanten Posts</h3>
                 <p className="max-w-sm text-sm text-gray-500">
                    Plane neue Posts, um deinen Content-Kalender zu füllen.
                 </p>
              </div>
            )}
          </div>
        </section>

        {/* Section 3: Veröffentlichte Posts */}
        <section className="space-y-6 pt-6">
          <SectionHeader
            icon={History}
            title="Zuletzt veröffentlicht"
            description="Deine erfolgreichen Posts im Rückblick."
            rightElement={
              <Link to="/post-generator/published" className="flex items-center gap-1 text-sm font-semibold text-gray-600 transition-colors hover:text-gray-950">
                Alle vergangenen Posts
                <ArrowRight className="w-4 h-4" />
              </Link>
            }
          />
           <div className={START_GRID_CLASS_NAME}>
            {/* Loading State or Data */}
            {!isMockMode && isLoadingRecent ? (
               Array.from({ length: visibleGridItemLimit }).map((_, i) => (
                <div key={i} className="flex flex-col gap-3">
                  <Skeleton className="w-full aspect-square rounded-xl" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-3/4 rounded-full" />
                    <Skeleton className="h-3 w-1/2 rounded-full" />
                  </div>
                </div>
              ))
            ) : publishedVisible.length > 0 ? (
               publishedVisible.map((post: any) => (
                <DataViewGridCard 
                  key={post.id} 
                  item={post} 
                  actions={{
                    ...mockActions,
                    onItemClick: () => handleRecentPostClick(post.id)
                  }}
                  className={CARD_CLASS_NAME}
                />
              ))
            ) : (
               <div className="col-span-full flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 bg-white py-12 text-center">
                 <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg border border-gray-200 bg-gray-50">
                   <History className="h-5 w-5 text-gray-400" />
                 </div>
                 <h3 className="mb-1 text-sm font-semibold text-gray-900">Noch keine Posts veröffentlicht</h3>
                 <p className="max-w-sm text-sm text-gray-500">
                    Sobald du Posts veröffentlichst, erscheinen diese hier im Rückblick.
                 </p>
              </div>
            )}
          </div>
        </section>

        <PlannedPostDialog 
          open={isPlannedDialogOpen} 
          onClose={() => setIsPlannedDialogOpen(false)} 
          post={selectedPlannedPost} 
        />
        <RecentPostDialog 
          open={isRecentDialogOpen} 
          onClose={() => setIsRecentDialogOpen(false)} 
          post={selectedRecentPost} 
        />
        <UpgradePlanDialog
          open={showUpgradeDialog}
          onOpenChange={setShowUpgradeDialog}
          requiredPlan={PlanKey.PRO}
        />
      </div>
    </div>
  );
};
